import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  OnInit,
  OnDestroy,
  viewChild,
} from '@angular/core';
import { DataEntity } from '@barthauer/abc/graphics/adapters/open-layers';
import {
  _AbcGalleryComponent,
  _AbcToolbarComponent,
  AbcSplitterV2Component,
  AbcPopupComponent,
  AbcTabPanelComponent,
  _AbcFormComponent,
  _AbcGridComponent,
  AbcDropDownBoxComponent,
  _AbcCheckBoxComponent,
  AbcWindowComponent,
} from '@barthauer/abc/ui-components';
import { DxToolbarModule, DxPopupModule, DxFormModule, DxTabPanelModule, DxNumberBoxModule } from 'devextreme-angular';
import { DataService } from '../services/data.service';
import {
  ClickableData,
  PipeData,
  PipeGraphicsSettings,
  PipeGraphicsSettingsViewModel,
  PipeGraphicsState,
  PipeGraphicsTable,
  StringLiteral,
} from '../models/misc.models';
import { ResourceSet } from '@barthauer/abc/core/resources';
import { ResizeEvent } from 'devextreme/ui/splitter';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { PromiseHelper } from '@barthauer/abc/core';
import { CoreService } from 'libs/abc/core/core.service';
import { DxiTabPanelItemComponent } from 'devextreme-angular/ui/tab-panel';
import { SelectionChangingEvent } from 'devextreme/ui/tab_panel';
import { OptionChangedEvent } from 'devextreme/ui/data_grid';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';
import { DataAccessComponent, DataAccessDefinition, ScalableImageComponent } from '@barthauer/basys/graphics/core';
import { DxiItemModule } from 'devextreme-angular/ui/nested';

declare const PanoramoViewer: any;

@Component({
  selector: 'lib-basys-graphics-wastewater-pipe-graphics',
  standalone: true,
  imports: [
    _AbcToolbarComponent,
    AbcSplitterV2Component,
    DxToolbarModule,
    DataAccessComponent,
    ScalableImageComponent,
    _AbcGalleryComponent,
    AbcPopupComponent,
    DxPopupModule,
    AbcTabPanelComponent,
    DxTabPanelModule,
    DxiTabPanelItemComponent,
    DxFormModule,
    DxiItemModule,
    _AbcGridComponent,
    AbcDropDownBoxComponent,
    _AbcCheckBoxComponent,
    DxNumberBoxModule,
  ],
  templateUrl: './pipe-graphics.component.html',
  styleUrl: './pipe-graphics.component.scss',
})
export class PipeGraphicsComponent implements OnInit, AfterViewInit, OnDestroy {
  get entity(): DataEntity {
    return this._entity;
  }
  set entity(entity: DataEntity) {
    if (this._entity !== entity) {
      const isNextPipe = !!this._entity;
      this._entity = entity;

      if (isNextPipe) {
        this.queryGraphics(entity.id, '', true);
      }
    }
  }
  private _entity: DataEntity;

  resourceSet = ResourceSet.Empty;
  private dataService = inject(DataService);
  private abcCoreService = inject(CoreService);
  private pipeGraphicsWindowInstance: AbcWindowComponent = null;
  private dataAccess = viewChild.required<DataAccessComponent>('dataAccess');
  dataAccessElement = viewChild.required('dataAccess', { read: ElementRef });
  private toolbar = viewChild.required<_AbcToolbarComponent>('toolbar');
  private outerSplitter = viewChild.required<AbcSplitterV2Component>('outerSplitter');
  private tabPanel = viewChild.required<AbcTabPanelComponent>('tabPanel');
  private scalableImageSocketBranch =
    viewChild.required<ScalableImageComponent<ClickableData>>('scalableImageSocketBranch');
  private scalableImageInspection =
    viewChild.required<ScalableImageComponent<ClickableData>>('scalableImageInspection');
  private gridConnectedPipes = viewChild.required<_AbcGridComponent>('gridConnectedPipes');
  private gridInspectionTexts = viewChild.required<_AbcGridComponent>('gridInspectionTexts');
  private gridRehabilitationMeasures = viewChild.required<_AbcGridComponent>('gridRehabilitationMeasures');
  private videoElement = viewChild.required<ElementRef<HTMLVideoElement>>('video');

  private panoramoDivElement = viewChild.required<ElementRef<HTMLDivElement>>('panoramoDiv');
  private panoramoViewer: any = null;
  private isPanoramoLoaded = false;

  pipeData = new PipeData();
  pipeGraphicsSettings = new PipeGraphicsSettingsViewModel(new PipeGraphicsSettings());
  private pipeGraphicsTables: PipeGraphicsTable[] = [];
  private pipeGraphicsStates: PipeGraphicsState[] = [];
  private pipeGraphicsState: PipeGraphicsState;
  private pipeId = '';
  private currentConnectedPipe?: ClickableData;
  private currentInspectionText?: ClickableData;
  private currentRehabilitationMeasure?: ClickableData;
  private currentInspectionTextPhotoUrls: string[] = [];
  photoUrls: string[] = [];
  private currentVideoUrl: string;
  private getMediaMaybe = true;

  tableRowsPipe: any[];
  tableRowsInspection: any[];
  tableRowsRehabilitation: any[];
  isPipeTable = false;
  isInspectionTable = false;
  isRehabilitationTable = false;

  get showIndicatorInGallery(): boolean {
    return this.photoUrls.length > 1;
  }
  get isLoadPanelVisible(): boolean {
    return (
      this.isQueryGraphicsPending || this.isQueryReportPending || this.isQueryPhotosPending || this.isQueryVideoPending
      // || this.isPersistSettingsPending // enable this if persistSettings() should block the user interface
    );
  }
  private isQueryGraphicsPending = true;
  private isQueryReportPending = false;
  private isQueryPhotosPending = false;
  private isQueryVideoPending = false;
  private isPersistSettingsPending = false;

  private showTable = false;
  private showMedia = false;
  isCollapsedGraphicsPane = false;
  isCollapsedInfoPane = true;
  isCollapsedTablePane = false;
  isCollapsedImageAndVideoPane = false;
  isCollapsedImagePane = false;
  isCollapsedVideoPane = false;
  isCollapsibleGraphicsPane = false;
  isCollapsibleInfoPane = false;
  isCollapsibleTablePane = true;
  isCollapsibleImageAndVideoPane = true;
  orientationImageAndVideoPane: any = 'horizontal';

  isSettingsPopupVisible = false;
  private acceptSettingsPopupChanges = false;
  private lastDOMRect: DOMRect;
  private lastWasMaximizedOrUnmaximized: boolean | null = null;

  isViewSocketBranchDisabled = false;
  isViewInspectionDisabled = false;

  toolbarRefreshButtonOptions: any;
  toolbarResetZoomButtonOptions: any;
  toolbarPrintButtonOptions: any;
  toolbarTableButtonOptions: any;
  toolbarMediaButtonOptions: any;
  toolbarSettingsButtonOptions: any;
  toolbarConfigurationGroupSelectBoxOptions: any;
  toolbarAdditionalIdSelectBoxOptions: any;

  settingsPopupAcceptButtonOptions: any;
  settingsPopupCancelButtonOptions: any;

  loadPanelPosition: any;

  isCompact = false;
  private resizeObserver: ResizeObserver;

  constructor(private pipeGraphicsComponentElement: ElementRef<HTMLElement>) {}

  ngOnDestroy(): void {
    this.pipeGraphicsWindowInstance?.closeDialog();
    this.isSettingsPopupVisible = false;
    this.resizeObserver?.disconnect();
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      const width = entries[0].contentRect.width;
      this.isCompact = width < 800;
    });

    this.resizeObserver.observe(this.toolbar().instance.element());
  }

  async ngOnInit() {
    this.resourceSet = await this.dataAccess().getResourceSet('basys-graphics-wastewater');
    this.initializeComponent(this.resourceSet);
    this.queryGraphics(this.entity.id, '', true);
    const parentPopupWindow = this.pipeGraphicsComponentElement.nativeElement.parentElement;

    if (!parentPopupWindow) {
      return;
    }

    const resizeObserver = new ResizeObserver((entries, observer) => {
      if (!entries.length || !entries[0].target) {
        return;
      }

      if (!this.loadPanelPosition) {
        this.loadPanelPosition = {
          at: 'center',
          my: 'center',
          of: this.dataAccessElement().nativeElement,
        };
      }

      const parentWindow = entries[0].target.parentElement;
      const imageTabsContainer = this.getImageTabsContainer();

      if (!parentWindow || !imageTabsContainer) {
        return;
      }

      const parentHeight = entries[0].contentRect.height;
      const toolbarHeight = this.toolbar().instance.element().getBoundingClientRect().height;
      const outerSplitter = this.outerSplitter();
      outerSplitter._setOption('height', parentHeight - toolbarHeight);

      const wasMaximizedOrUnmaximized = this.wasMaximizedOrUnmaximized(parentWindow);
      this.getAllClickableImages().forEach((clickableImage) =>
        clickableImage.size.set({
          width: imageTabsContainer.clientWidth,
          height: imageTabsContainer.clientHeight,
          resetImage:
            wasMaximizedOrUnmaximized ||
            this.lastWasMaximizedOrUnmaximized === false ||
            clickableImage.zoomLevel <= 1.0,
        }),
      );
      this.lastWasMaximizedOrUnmaximized = wasMaximizedOrUnmaximized;
    });
    resizeObserver.observe(parentPopupWindow);
  }

  private initializeComponent(resourceSet: ResourceSet): void {
    this.toolbarRefreshButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Refresh-Image'),
      hint: resourceSet.get('Refresh'),
      onClick: () => {
        this.queryGraphics(this.pipeId, this.getView(), true);
      },
    };
    this.toolbarResetZoomButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('ResetZoom-Image'),
      hint: resourceSet.get('ResetZoom'),
      onClick: () => {
        this.getAllClickableImages().forEach((clickableImage) => clickableImage.resetImage());
      },
    };
    this.toolbarPrintButtonOptions = {
      disabled: false,
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Print-Image'),
      hint: resourceSet.get('Print'),
      onClick: () => {
        this.queryReport(this.pipeId, this.pipeGraphicsState.reportFilename);
      },
    };
    this.toolbarTableButtonOptions = {
      type: 'normal',
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Table-Image'),
      hint: resourceSet.get('Table'),
      onClick: () => {
        this.setSplittersCollapsed(!this.showTable, null);
        this.toolbarTableButtonOptions.type = this.showTable ? 'default' : 'normal';
        this.resetToolbarTableButtonOptions();
        this.persistSettings(this.pipeId, this.getView());
        setTimeout(() => this.getAllClickableImages().forEach((clickableImage) => clickableImage.resetImage()));
      },
    };
    this.toolbarMediaButtonOptions = {
      type: 'normal',
      disabled: false,
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Media-Image'),
      hint: resourceSet.get('Media'),
      onClick: () => {
        this.setSplittersCollapsed(null, !this.showMedia);
        this.toolbarMediaButtonOptions.type = this.showMedia ? 'default' : 'normal';

        if (!this.pipeGraphicsState.inspectionId) {
          this.toolbarMediaButtonOptions.disabled = true;
        }

        this.resetToolbarMediaButtonOptions();

        if (this.showMedia) {
          if (this.currentInspectionText?.hasPhoto) {
            this.queryPhotos(this.pipeId, this.currentInspectionText.id);
          }

          this.queryVideo(
            this.pipeId,
            this.currentInspectionText?.hasVideo ? this.currentInspectionText.id : undefined,
          );
        } else {
          this.currentInspectionTextPhotoUrls = [];
          this.photoUrls = [];
          this.clearVideoControl();
          this.persistSettings(this.pipeId, this.getView());
        }

        setTimeout(() => this.getAllClickableImages().forEach((clickableImage) => clickableImage.resetImage()));
      },
    };
    this.toolbarSettingsButtonOptions = {
      focusStateEnabled: false,
      icon: resourceSet.svgUrl('Settings-Image'),
      hint: resourceSet.get('Settings'),
      onClick: () => {
        const configurationGroupEntryInspection = this.pipeGraphicsSettings.configurationGroupEntryInspection;
        this.pipeGraphicsSettings.configurationGroupEntryInspection = '';
        setTimeout(() => {
          this.pipeGraphicsSettings.configurationGroupEntryInspection = configurationGroupEntryInspection;
          this.acceptSettingsPopupChanges = false;
          this.isSettingsPopupVisible = true;
        });
      },
    };
    this.settingsPopupAcceptButtonOptions = {
      stylingMode: 'outlined',
      text: resourceSet.get('Accept'),
      onClick: () => {
        this.acceptSettingsPopupChanges = true;
        this.isSettingsPopupVisible = false;
      },
    };
    this.settingsPopupCancelButtonOptions = {
      stylingMode: 'outlined',
      text: resourceSet.get('Cancel'),
      onClick: () => {
        this.acceptSettingsPopupChanges = false;
        this.isSettingsPopupVisible = false;
      },
    };
  }

  onSelectionChanging(e: SelectionChangingEvent): void {
    if (this.suspendSelectionChanging) {
      if (this.resumeSelectionChanging) {
        this.resumeSelectionChanging = false;
        this.suspendSelectionChanging = false;
      }

      return;
    }

    e.cancel = true;

    const oldTabTitle = e.removedItems.length > 0 ? (e.removedItems[0].title ?? '') : '';
    const newTabTitle = e.addedItems.length > 0 ? (e.addedItems[0].title ?? '') : '';

    if (newTabTitle && newTabTitle !== oldTabTitle) {
      let view = '';

      if (newTabTitle === this.resourceSet.get('SocketBranch')) {
        view = StringLiteral.ViewSocketBranch;
      } else if (newTabTitle === this.resourceSet.get('InspectionRehabilitation')) {
        view = StringLiteral.ViewInspection;
      }

      if (view) {
        this.queryGraphics(this.pipeId, view, false);
      }
    }
  }
  private suspendSelectionChanging = false;
  private resumeSelectionChanging = false;

  private queryGraphics(pipeId: string, view: string, isInitial: boolean): void {
    this.suspendSelectionChanging = true;
    this.suspendToolbarSelectBoxesValueChanged = true;
    const isViewChange = view !== this.getView();
    const additionalId =
      !isViewChange && view === StringLiteral.ViewInspection
        ? (this.toolbarAdditionalIdSelectBoxOptions?.value ?? '')
        : '';
    const currentSettings = view ? this.getSettings(isViewChange) : undefined;

    if (isInitial && this.pipeGraphicsState) {
      this.currentConnectedPipe = undefined;
      this.currentInspectionText = undefined;
      this.currentRehabilitationMeasure = undefined;
      this.currentInspectionTextPhotoUrls = [];
      this.photoUrls = [];
      this.clearVideoControl();
      this.resetAllFocusedRows();
      this.getAllClickableImages().forEach((clickableImage) => clickableImage.clearMarking());
      this.tableRowsPipe = [];
      this.tableRowsInspection = [];
      this.tableRowsRehabilitation = [];
      this.pipeGraphicsTables.length = 0;
      this.pipeGraphicsTables = [];
      this.getMediaMaybe = true;
    }

    if (this.pipeGraphicsStates) {
      if (isInitial) {
        this.pipeGraphicsStates.length = 0;
        this.pipeGraphicsStates = [];
      } else {
        const stateMatch = this.pipeGraphicsStates.find((state, index) => {
          if (state.view === view) {
            if (isViewChange) {
              return state.isActive;
            } else {
              switch (view) {
                case StringLiteral.ViewSocketBranch:
                  return true;
                case StringLiteral.ViewInspection:
                  return (
                    state.configurationGroup === currentSettings.configurationGroupInspection &&
                    state.configurationGroupEntry === currentSettings.configurationGroupEntryInspection &&
                    (!state.table.isInspection ||
                      state.hideStatusTextsIfNotRelevant === currentSettings.hideStatusTextsIfNotRelevant) &&
                    (state.additionalIds.length === 0 ||
                      (additionalId && additionalId === state.additionalId) ||
                      (state.isActiveAdditionalId &&
                        (!additionalId ||
                          !state.additionalIds.some((idAndDisplayText) => idAndDisplayText.id === additionalId))))
                  );
              }
            }
          }

          return false;
        });

        if (stateMatch) {
          if (!isViewChange) {
            for (const state of this.pipeGraphicsStates.filter((s) => s.view === view)) {
              state.isActive = false;
            }

            stateMatch.isActive = true;

            if (
              view === StringLiteral.ViewInspection &&
              stateMatch.additionalIds.length > 0 &&
              !stateMatch.isActiveAdditionalId
            ) {
              this.setActiveAdditionalId(stateMatch);
            }
          }

          const clickableImage = this.getClickableImage(view);
          clickableImage.clickableFeatures.set(stateMatch.clickableData);
          clickableImage.image.set({
            imageBase64: stateMatch.imageBase64,
            width: stateMatch.imageWidth,
            height: stateMatch.imageHeight,
            initialMarking: this.refreshCurrentClickableData(stateMatch.table, stateMatch.clickableData),
          });
          this.refreshTableRows(stateMatch.table);

          if (view === StringLiteral.ViewInspection) {
            this.refreshToolbarAdditionalIdSelectBox(stateMatch);
          }

          if (isViewChange) {
            this.refreshToolbarSelectBoxes(view, stateMatch.configurationGroup);
            this.resumeSelectionChanging = true;
            setTimeout(() => this.changeView(view));
          } else {
            this.suspendSelectionChanging = false;
          }

          this.pipeGraphicsState = stateMatch;
          this.setMediaButtonEnabled();
          this.setPrintButtonEnabled(view);
          this.persistSettings(pipeId, view, stateMatch.additionalId ?? '', currentSettings);
          setTimeout(() => (this.suspendToolbarSelectBoxesValueChanged = false), 100);

          return;
        }
      }
    }

    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryGraphics',
        pipeId,
        (isPending) => {
          this.isQueryGraphicsPending = isPending;

          if (!isPending) {
            this.suspendSelectionChanging = false;
          }
        },
        undefined,
        async (data) => {
          if (pipeId !== this.entity.id) {
            return;
          }

          view = data[0].view;
          this.pipeId = pipeId;
          let pipeGraphicsTable = this.pipeGraphicsTables.find((table) => table.id === data[0].table.id);

          if (!pipeGraphicsTable) {
            pipeGraphicsTable = new PipeGraphicsTable(data[0].table);
            this.pipeGraphicsTables.push(pipeGraphicsTable);
          }

          const pipeGraphicsState = new PipeGraphicsState();
          this.pipeGraphicsState = pipeGraphicsState;
          pipeGraphicsState.isActive = true;
          pipeGraphicsState.view = view;
          pipeGraphicsState.table = pipeGraphicsTable;
          pipeGraphicsState.inspectionId = data[0].idAbwLeitungsinspektion;
          pipeGraphicsState.rehabilitationId = data[0].idAbwSanierungsalternative;
          pipeGraphicsState.reportFilename = data[0].reportFilename;

          if (this.pipeGraphicsStates) {
            for (const state of this.pipeGraphicsStates.filter((s) => s.view === view)) {
              state.isActive = false;
            }

            this.pipeGraphicsStates.push(pipeGraphicsState);
          }

          if (isInitial) {
            this.pipeData = new PipeData(data[0].pipeData);
            this.pipeGraphicsSettings = new PipeGraphicsSettingsViewModel(new PipeGraphicsSettings(data[0].settings));

            this.isViewSocketBranchDisabled = !data[0].availableViews.some(
              (view) => view === StringLiteral.ViewSocketBranch,
            );
            this.isViewInspectionDisabled = !data[0].availableViews.some(
              (view) => view === StringLiteral.ViewInspection,
            );

            const tabPanel = this.tabPanel();
            tabPanel.items[0].disabled = this.isViewSocketBranchDisabled;
            tabPanel.items[1].disabled = this.isViewInspectionDisabled;

            if (!this.isViewInspectionDisabled) {
              this.pipeGraphicsSettings.refreshConfigurationGroupEntriesInspection(
                this.pipeGraphicsSettings.pipeGraphicsSettings.configurationGroupEntriesInspection[0]
                  .configurationGroup,
              );
            }

            this.setSplittersCollapsed(
              this.pipeGraphicsSettings.pipeGraphicsSettings.showTable,
              this.pipeGraphicsSettings.pipeGraphicsSettings.showMedia,
            );

            if (this.showTable && this.toolbarTableButtonOptions.type !== 'default') {
              this.toolbarTableButtonOptions.type = 'default';
              this.resetToolbarTableButtonOptions();
            }
          }

          if (isInitial || this.getMediaMaybe) {
            if (
              !isInitial &&
              !this.pipeGraphicsSettings.pipeGraphicsSettings.showMedia &&
              data[0].settings?.showMedia
            ) {
              this.pipeGraphicsSettings.pipeGraphicsSettings.showMedia = true;
              this.setSplittersCollapsed(null, true);
            }

            if (this.showMedia) {
              if (
                this.toolbarMediaButtonOptions.disabled !== false ||
                this.toolbarMediaButtonOptions.type !== 'default'
              ) {
                this.toolbarMediaButtonOptions.disabled = false;
                this.toolbarMediaButtonOptions.type = 'default';
                this.resetToolbarMediaButtonOptions();
              }

              try {
                await this.setVideo(data[0]);
              } catch (err) {
                this.dataAccess().errorService.publish(err);
              }
            }
          }

          if ((!isInitial && !this.getMediaMaybe) || !this.showMedia) {
            this.setMediaButtonEnabled();
          }

          if (this.pipeGraphicsState.inspectionId) {
            this.getMediaMaybe = false;
          }

          await PromiseHelper.delay(async () => {
            pipeGraphicsState.imageBase64 = 'data:image/png;base64,' + data[0].imageBase64;
            pipeGraphicsState.imageWidth = data[0].imageWidth;
            pipeGraphicsState.imageHeight = data[0].imageHeight;
            pipeGraphicsState.clickableData = [];

            if (data[0].features) {
              for (const feature of data[0].features) {
                var clickableFeature = new ClickableData(feature);
                pipeGraphicsState.clickableData.push(clickableFeature);
              }
            }

            const clickableImage = this.getClickableImage(view);
            clickableImage.clickableFeatures.set(pipeGraphicsState.clickableData);
            clickableImage.image.set({
              imageBase64: pipeGraphicsState.imageBase64,
              width: pipeGraphicsState.imageWidth,
              height: pipeGraphicsState.imageHeight,
              initialMarking: this.refreshCurrentClickableData(pipeGraphicsTable, pipeGraphicsState.clickableData),
            });
            this.refreshTableRows(pipeGraphicsTable);

            if (isInitial || isViewChange) {
              this.refreshToolbarSelectBoxes(view, data[0].configurationGroup);
            }

            switch (view) {
              case StringLiteral.ViewSocketBranch:
                break;
              case StringLiteral.ViewInspection:
                pipeGraphicsState.configurationGroup = data[0].configurationGroup;
                pipeGraphicsState.configurationGroupEntry = this.pipeGraphicsSettings.configurationGroupEntryInspection;

                if (pipeGraphicsTable.isInspection) {
                  pipeGraphicsState.hideStatusTextsIfNotRelevant =
                    this.pipeGraphicsSettings.hideStatusTextsIfNotRelevant;
                }

                pipeGraphicsState.additionalIds = data[0].additionalIds ?? [];
                pipeGraphicsState.additionalId = data[0].additionalId ?? '';

                if (pipeGraphicsState.additionalId) {
                  this.setActiveAdditionalId(pipeGraphicsState);
                }

                this.refreshToolbarAdditionalIdSelectBox(pipeGraphicsState);
                break;
            }

            if (isInitial || isViewChange) {
              this.changeView(view);
            }

            this.setPrintButtonEnabled(view);
            setTimeout(() => (this.suspendToolbarSelectBoxesValueChanged = false), 100);
          }, 0);
        },
        [
          () =>
            this.dataService.queryPipeGraphics(
              view,
              pipeId,
              additionalId,
              this.pipeGraphicsTables.map((table) => table.id),
              currentSettings,
              this.getMediaMaybe,
              isInitial,
            ),
        ],
      ),
    );
  }

  private setActiveAdditionalId(pipeGraphicsState: PipeGraphicsState): void {
    if (this.pipeGraphicsStates) {
      for (const state of this.pipeGraphicsStates.filter(
        (s) =>
          s.view === StringLiteral.ViewInspection &&
          s.configurationGroup === pipeGraphicsState.configurationGroup &&
          s.configurationGroupEntry === pipeGraphicsState.configurationGroupEntry,
      )) {
        state.isActiveAdditionalId = state.additionalId === pipeGraphicsState.additionalId;
      }
    }
  }

  private refreshToolbarSelectBoxes(view: string, configurationGroup: string): void {
    switch (view) {
      case StringLiteral.ViewSocketBranch:
        this.handleConfigurationGroupChanged = undefined;
        this.handleAdditionalIdChanged = undefined;
        this.toolbarConfigurationGroupSelectBoxOptions = this.createSelectBoxOptions(null, null);
        this.toolbarAdditionalIdSelectBoxOptions = this.createSelectBoxOptions(null, null);
        break;
      case StringLiteral.ViewInspection:
        this.handleConfigurationGroupChanged = (_configurationGroup) => {
          this.pipeGraphicsSettings.refreshConfigurationGroupEntriesInspection(_configurationGroup);
          this.queryGraphics(this.pipeId, StringLiteral.ViewInspection, false);
        };
        this.toolbarConfigurationGroupSelectBoxOptions = this.createSelectBoxOptions(
          this.pipeGraphicsSettings.pipeGraphicsSettings.configurationGroupsInspection,
          configurationGroup,
        );
        break;
    }
  }

  private refreshToolbarAdditionalIdSelectBox(pipeGraphicsState: PipeGraphicsState): void {
    let label = '';

    if (pipeGraphicsState.rehabilitationId) {
      label = this.resourceSet.get('Sanierungsalternative');
    } else if (pipeGraphicsState.additionalId) {
      label = this.resourceSet.get('SecondInspection');
    }

    this.handleAdditionalIdChanged = (additionalId) => {
      this.queryGraphics(this.pipeId, StringLiteral.ViewInspection, false);
    };

    this.toolbarAdditionalIdSelectBoxOptions = this.createSelectBoxOptions(
      pipeGraphicsState.additionalIds,
      pipeGraphicsState.additionalId,
      label,
    );
  }

  handleToolbarConfigurationGroupSelectBoxValueChanged(e: DxSelectBoxTypes.ValueChangedEvent): void {
    if (!this.suspendToolbarSelectBoxesValueChanged && this.handleConfigurationGroupChanged) {
      this.handleConfigurationGroupChanged(e.value);
    }
  }

  handleToolbarAdditionalIdSelectBoxValueChanged(e: DxSelectBoxTypes.ValueChangedEvent): void {
    if (!this.suspendToolbarSelectBoxesValueChanged && this.handleAdditionalIdChanged) {
      this.handleAdditionalIdChanged(e.value);
    }
  }

  private handleConfigurationGroupChanged: (configurationGroup: string) => void;
  private handleAdditionalIdChanged: (additionalId: string) => void;
  private suspendToolbarSelectBoxesValueChanged = false;

  handleSettingsPopupConfigurationEntrySelectBoxValueChanged(e: DxSelectBoxTypes.ValueChangedEvent): void {
    if (this.isSettingsPopupVisible) {
      this.pipeGraphicsSettings.configurationGroupEntryInspection = e.value;
    }
  }

  private queryReport(pipeId: string, reportFilename: string): void {
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryReport',
        pipeId,
        (isPending) => {
          this.isQueryReportPending = isPending;
        },
        (data) => {
          if (pipeId !== this.entity.id) {
            return;
          }

          if (data[0]?.size > 0) {
            const downloadUrl = URL.createObjectURL(data[0]);
            setTimeout(() => {
              const newWindow = this.abcCoreService.openWindow(downloadUrl);

              if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const downloadLink = document.children[0].appendChild(document.createElement('a'));
                downloadLink.setAttribute('download', reportFilename);
                downloadLink.setAttribute('href', downloadUrl);
                downloadLink.click();
              }
            }, 100);
          }
        },
        undefined,
        [
          () =>
            this.dataService.queryPipeReport(
              this.getView(),
              pipeId,
              this.toolbarAdditionalIdSelectBoxOptions.value,
              this.getSettings(false),
            ),
        ],
      ),
    );
  }

  private queryPhotos(pipeId: string, idAbwLeitungsinspektionstext: string): void {
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryPhotos',
        pipeId,
        (isPending) => {
          this.isQueryPhotosPending = isPending;
        },
        (data) => {
          if (pipeId !== this.entity.id) {
            return;
          }

          if (data[0].photoUrls) {
            this.currentInspectionTextPhotoUrls = [];

            for (const photoUrl of data[0].photoUrls) {
              this.currentInspectionTextPhotoUrls.push(photoUrl);
            }

            this.photoUrls = this.currentInspectionTextPhotoUrls;
            this.isCollapsedImagePane = false;
            this.isCollapsedImageAndVideoPane = false;
            this.isCollapsedInfoPane = false;
          }
        },
        undefined,
        [() => this.dataService.queryPipePhotos(idAbwLeitungsinspektionstext, this.getSettings(false))],
      ),
    );
  }

  private queryVideo(pipeId: string, idAbwLeitungsinspektionstext: string | undefined): void {
    const idAbwLeitungsinspektion = idAbwLeitungsinspektionstext ? '' : this.pipeGraphicsState.inspectionId;
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'queryVideo',
        pipeId,
        (isPending) => {
          this.isQueryVideoPending = isPending;
        },
        undefined,
        async (data) => {
          if (pipeId !== this.entity.id) {
            return;
          }

          await this.setVideo(data[0]);
          this.isCollapsedVideoPane = false;
          this.isCollapsedImageAndVideoPane = false;
          this.isCollapsedInfoPane = false;
        },
        [
          () =>
            this.dataService.queryPipeVideo(
              idAbwLeitungsinspektion,
              idAbwLeitungsinspektionstext,
              this.getSettings(false),
            ),
        ],
      ),
    );
  }

  private persistSettings(pipeId: string, view: string, additionalId?: string, settings?: any): void {
    this.dataAccess().callWebApi(
      new DataAccessDefinition(
        'persistSettings',
        pipeId,
        (isPending) => {
          this.isPersistSettingsPending = isPending;
        },
        (data) => {
          if (pipeId !== this.entity.id) {
            return;
          }
        },
        undefined,
        [
          () =>
            this.dataService.persistPipeSettings(
              view,
              additionalId ?? this.toolbarAdditionalIdSelectBoxOptions.value,
              settings ?? this.getSettings(false),
            ),
        ],
      ),
    );
  }

  private async setVideo(data: any): Promise<void> {
    const videoUrl = data?.videoUrl ?? '';
    const isPanoramo = data?.isPanoramo ?? false;

    if (isPanoramo) {
      const videoControl = this.videoElement().nativeElement;
      const panoramoDiv = this.panoramoDivElement().nativeElement;

      videoControl.style.visibility = 'hidden';
      videoControl.style.width = '0%';
      videoControl.style.height = '0%';
      videoControl.load();
      videoControl.src = '';
      videoControl.currentTime = 0;

      if (!this.panoramoViewer) {
        if (!this.isPanoramoLoaded) {
          this.isPanoramoLoaded = true;
          await this.dataService.loadPanoramo();
        }

        this.panoramoViewer = new PanoramoViewer({
          perspectiveDiv: panoramoDiv,
        });
        this.panoramoViewer.showNavigationLines();
        this.panoramoViewer.showPositionOverlay();
      }

      if (this.currentVideoUrl !== videoUrl) {
        this.currentVideoUrl = videoUrl;

        if (videoUrl) {
          await this.panoramoViewer.selectScan(videoUrl);
          await this.panoramoViewer.openPanoView();
          this.panoramoViewer.showDividingLine();
        } else if (this.panoramoViewer) {
          this.panoramoViewer.dispose();
          this.panoramoViewer = null;
        }
      }

      if (videoUrl) {
        const panoramoDistance = data?.panoramoDistance;
        const panoramoPolarAngle = data?.panoramoPolarAngle;
        const panoramoAzimutalAngle = data?.panoramoAzimutalAngle;

        if (panoramoDistance || panoramoPolarAngle || panoramoAzimutalAngle) {
          this.panoramoViewer.setPosition(panoramoDistance, panoramoPolarAngle, panoramoAzimutalAngle);
        }
      }

      panoramoDiv.style.width = '100%';
      panoramoDiv.style.height = '100%';
      panoramoDiv.style.visibility = 'visible';
    } else {
      const videoTime = data?.videoTime ?? 0;
      this.setVideoControl(videoUrl, videoTime);
    }
  }

  private setVideoControl(videoUrl: string, videoTime: number): void {
    const videoControl = this.videoElement().nativeElement;
    const panoramoDiv = this.panoramoDivElement().nativeElement;

    panoramoDiv.style.visibility = 'hidden';
    panoramoDiv.style.width = '0%';
    panoramoDiv.style.height = '0%';

    if (this.panoramoViewer) {
      this.panoramoViewer.dispose();
      this.panoramoViewer = null;
    }

    if (this.currentVideoUrl !== videoUrl) {
      this.currentVideoUrl = videoUrl;
      videoControl.load();
      videoControl.src = videoUrl;
    }

    videoControl.currentTime = videoTime;
    videoControl.style.width = '100%';
    videoControl.style.height = '100%';
    videoControl.style.visibility = 'visible';
  }

  private clearVideoControl(): void {
    this.setVideoControl('', 0);
  }

  private getSettings(isViewChange: boolean): any {
    const settings: any = {};

    settings.graphicsDocumentIdSocketBranch = this.pipeGraphicsSettings.graphicsDocumentIdSocketBranch;
    settings.graphicsDocumentIdInspection = this.pipeGraphicsSettings.graphicsDocumentIdInspection;

    settings.configurationGroupEntryInspection = this.pipeGraphicsSettings.configurationGroupEntryInspection;

    settings.reportDocumentIdSocketBranch = this.pipeGraphicsSettings.reportDocumentIdSocketBranch;
    settings.reportDocumentIdSingleInspection = this.pipeGraphicsSettings.reportDocumentIdSingleInspection;
    settings.reportDocumentIdDoubleInspection = this.pipeGraphicsSettings.reportDocumentIdDoubleInspection;
    settings.reportDocumentIdInspectionRehabilitation =
      this.pipeGraphicsSettings.reportDocumentIdInspectionRehabilitation;
    settings.reportDocumentIdSingleRehabilitation = this.pipeGraphicsSettings.reportDocumentIdSingleRehabilitation;

    settings.showPhotosInReport = this.pipeGraphicsSettings.showPhotosInReport;
    settings.hideHeaderInReport = this.pipeGraphicsSettings.hideHeaderInReport;
    settings.hideStatusTextsIfNotRelevant = this.pipeGraphicsSettings.hideStatusTextsIfNotRelevant;

    if (!isViewChange) {
      settings.showTable = this.showTable;

      if (this.pipeGraphicsState?.inspectionId) {
        settings.showMedia = this.showMedia;
      }

      switch (this.getView()) {
        case StringLiteral.ViewSocketBranch:
          break;
        case StringLiteral.ViewInspection:
          settings.configurationGroupInspection = this.toolbarConfigurationGroupSelectBoxOptions.value;
          break;
      }
    }

    return settings;
  }

  private setSplittersCollapsed(showTable: boolean | null, showMedia: boolean | null): void {
    const tableShown = showTable && showTable !== this.showTable;
    const mediaShown = showMedia && showMedia !== this.showMedia;

    if (showTable !== null) {
      this.showTable = showTable;
    }

    if (showMedia !== null) {
      this.showMedia = showMedia;
    }

    if (this.showTable && this.showMedia) {
      this.setTablePaneCollapsedCollapsible(tableShown ? false : null, true);
      this.setImageAndVideoPaneCollapsedCollapsible(mediaShown ? false : null, true);
      this.setInfoPaneCollapsedCollapsible(tableShown || mediaShown ? false : null, true);
      this.setGraphicsPaneCollapsedCollapsible(null, true);
      this.orientationImageAndVideoPane = 'horizontal';
    } else if (this.showTable) {
      this.setTablePaneCollapsedCollapsible(false, false);
      this.setInfoPaneCollapsedCollapsible(tableShown ? false : null, true);
      this.setGraphicsPaneCollapsedCollapsible(null, true);

      setTimeout(() => {
        this.setImageAndVideoPaneCollapsedCollapsible(true, false);
        this.orientationImageAndVideoPane = 'horizontal';
      });
    } else if (this.showMedia) {
      this.setImageAndVideoPaneCollapsedCollapsible(false, false);
      this.setInfoPaneCollapsedCollapsible(mediaShown ? false : null, true);
      this.setGraphicsPaneCollapsedCollapsible(null, true);

      setTimeout(() => {
        this.setTablePaneCollapsedCollapsible(true, false);
        this.orientationImageAndVideoPane = 'vertical';
      });
    } else {
      this.setTablePaneCollapsedCollapsible(false, true);
      this.setImageAndVideoPaneCollapsedCollapsible(false, true);
      this.setGraphicsPaneCollapsedCollapsible(false, false);

      setTimeout(() => {
        this.setInfoPaneCollapsedCollapsible(true, false);
        this.orientationImageAndVideoPane = 'horizontal';
      });
    }
  }

  private setGraphicsPaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedGraphicsPane = collapsed;
    }

    this.isCollapsibleGraphicsPane = collapsible;
  }

  private setInfoPaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedInfoPane = collapsed;
    }

    this.isCollapsibleInfoPane = collapsible;
  }

  private setTablePaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedTablePane = collapsed;
    }

    this.isCollapsibleTablePane = collapsible;
  }

  private setImageAndVideoPaneCollapsedCollapsible(collapsed: boolean | null, collapsible: boolean): void {
    if (collapsed !== null) {
      this.isCollapsedImageAndVideoPane = collapsed;
      this.isCollapsedImagePane = false;
      this.isCollapsedVideoPane = false;
    }

    this.isCollapsibleImageAndVideoPane = collapsible;
  }

  handleOuterSplitterCollapsedOrExpanded(e: any): void {
    if (!e.itemData) {
      return;
    }

    if (e.itemIndex === 0) {
      this.isCollapsedGraphicsPane = e.itemData.collapsed;
    } else if (e.itemIndex === 1) {
      this.isCollapsedInfoPane = e.itemData.collapsed;
    }

    this.resizePipeImage();
  }

  handleInfoSplitterCollapsedOrExpanded(e: any): void {
    if (!e.itemData) {
      return;
    }

    if (e.itemIndex === 0) {
      this.isCollapsedTablePane = e.itemData.collapsed;
    } else if (e.itemIndex === 1) {
      this.isCollapsedImageAndVideoPane = e.itemData.collapsed;
    }
  }

  handleImageAndVideoSplitterCollapsedOrExpanded(e: any): void {
    if (!e.itemData) {
      return;
    }

    if (e.itemIndex === 0) {
      this.isCollapsedImagePane = e.itemData.collapsed;
    } else if (e.itemIndex === 1) {
      this.isCollapsedVideoPane = e.itemData.collapsed;
    }
  }

  private refreshTableRows(table: PipeGraphicsTable): void {
    if (table.isPipe) {
      this.tableRowsPipe = table.rows;
      this.isInspectionTable = false;
      this.isRehabilitationTable = false;
      this.isPipeTable = true;
    } else if (table.isInspection) {
      this.tableRowsInspection = table.rows;
      this.isPipeTable = false;
      this.isRehabilitationTable = false;
      this.isInspectionTable = true;
    } else if (table.isRehabilitation) {
      this.tableRowsRehabilitation = table.rows;
      this.isPipeTable = false;
      this.isInspectionTable = false;
      this.isRehabilitationTable = true;
    }
  }

  private createSelectBoxOptions(items: any, selectedItem: string | null, label?: string): any {
    const itemArray = [];

    if (items) {
      for (const item of items) {
        itemArray.push({ id: item.id, text: item.displayText });
      }
    }

    return {
      items: itemArray,
      value: selectedItem,
      disabled: itemArray.length === 0,
      label: label,
      displayExpr: 'text',
      valueExpr: 'id',
      width: '100%',
      gridColumns: [{ dataField: 'text', caption: '' }],
    };
  }

  handleSettingsPopupHidden(): void {
    if (!this.acceptSettingsPopupChanges || !this.pipeGraphicsSettings.hasChanges) {
      this.pipeGraphicsSettings.reset();
      return;
    }

    let isInitial =
      this.pipeGraphicsSettings.graphicsDocumentIdSocketBranchHasChanged ||
      this.pipeGraphicsSettings.graphicsDocumentIdInspectionHasChanged
        ? true
        : null;
    const view = this.getView();

    if (!isInitial) {
      switch (view) {
        case StringLiteral.ViewSocketBranch:
          break;
        case StringLiteral.ViewInspection:
          if (
            this.pipeGraphicsSettings.configurationGroupEntryInspectionHasChanged ||
            (this.pipeGraphicsState.table.isInspection &&
              this.pipeGraphicsSettings.hideStatusTextsIfNotRelevantHasChanged)
          ) {
            isInitial = false;
          }
          break;
      }
    }

    if (this.pipeGraphicsStates) {
      if (
        this.pipeGraphicsSettings.configurationGroupEntryInspectionHasChanged ||
        this.pipeGraphicsSettings.hideStatusTextsIfNotRelevantHasChanged
      ) {
        const oldActiveState = this.pipeGraphicsStates.find(
          (state) => state.view === StringLiteral.ViewInspection && state.isActive,
        );

        if (oldActiveState) {
          oldActiveState.isActive = false;
          const newActiveState = this.pipeGraphicsStates.find(
            (state) =>
              state.view === StringLiteral.ViewInspection &&
              state.configurationGroup === oldActiveState.configurationGroup &&
              state.configurationGroupEntry === this.pipeGraphicsSettings.configurationGroupEntryInspection &&
              (!state.table.isInspection ||
                state.hideStatusTextsIfNotRelevant === this.pipeGraphicsSettings.hideStatusTextsIfNotRelevant) &&
              (oldActiveState.additionalIds.length === 0 || oldActiveState.additionalId === state.additionalId),
          );

          if (newActiveState) {
            newActiveState.isActive = true;

            if (newActiveState.additionalId) {
              this.setActiveAdditionalId(newActiveState);
            }
          }
        }
      }
    }

    if (this.pipeGraphicsSettings.configurationGroupEntryInspectionHasChanged) {
      this.pipeGraphicsSettings.configurationGroupEntriesInspection.forEach(
        (configurationGroupEntry) =>
          (configurationGroupEntry.isActive =
            configurationGroupEntry.id === this.pipeGraphicsSettings.configurationGroupEntryInspection),
      );
    }

    if (
      this.pipeGraphicsSettings.reportDocumentIdSocketBranchHasChanged ||
      this.pipeGraphicsSettings.reportDocumentIdSingleInspectionHasChanged ||
      this.pipeGraphicsSettings.reportDocumentIdDoubleInspectionHasChanged ||
      this.pipeGraphicsSettings.reportDocumentIdInspectionRehabilitationHasChanged ||
      this.pipeGraphicsSettings.reportDocumentIdSingleRehabilitationHasChanged
    ) {
      this.setPrintButtonEnabled(view);
    }

    this.pipeGraphicsSettings.accept();

    if (isInitial === null) {
      this.persistSettings(this.pipeId, view);
    } else {
      this.queryGraphics(this.pipeId, view, isInitial);
    }
  }

  private refreshCurrentClickableData(
    table: PipeGraphicsTable,
    clickableFeatures: ClickableData[],
  ): ClickableData | undefined {
    if (table.isPipe || table.isRehabilitation) {
      this.photoUrls = [];
    }

    if (table.isPipe && this.currentConnectedPipe?.id) {
      this.currentConnectedPipe = clickableFeatures.find((feature) => feature.id === this.currentConnectedPipe.id);

      if (!this.currentConnectedPipe) {
        this.resetFocusedRow(this.gridConnectedPipes());
      }

      return this.currentConnectedPipe;
    } else if (table.isInspection && this.currentInspectionText?.id) {
      this.currentInspectionText = clickableFeatures.find((feature) => feature.id === this.currentInspectionText.id);

      if (this.currentInspectionText) {
        this.photoUrls = this.currentInspectionTextPhotoUrls;
      } else {
        this.resetFocusedRow(this.gridInspectionTexts());
        this.currentInspectionTextPhotoUrls = [];
        this.photoUrls = [];
      }

      return this.currentInspectionText;
    } else if (table.isRehabilitation && this.currentRehabilitationMeasure?.id) {
      this.currentRehabilitationMeasure = clickableFeatures.find(
        (feature) => feature.id === this.currentRehabilitationMeasure.id,
      );

      if (!this.currentRehabilitationMeasure) {
        this.resetFocusedRow(this.gridRehabilitationMeasures());
      }

      return this.currentRehabilitationMeasure;
    }

    return undefined;
  }

  handleFeatureClicked(clickedData: ClickableData | undefined, isGridCall: boolean = false): void {
    const table = this.pipeGraphicsState.table;

    if (!isGridCall) {
      try {
        this.suspendFocusedRowChanged = true;
        this.getDataGrid(table).focusedRowKey = clickedData?.id;
      } finally {
        this.suspendFocusedRowChanged = false;
      }
    }

    if (table.isPipe) {
      this.currentConnectedPipe = clickedData;
    } else if (table.isInspection) {
      this.currentInspectionText = clickedData;
      this.currentInspectionTextPhotoUrls = [];
      this.photoUrls = [];

      if (clickedData && this.showMedia) {
        if (clickedData.hasPhoto) {
          this.queryPhotos(this.pipeId, clickedData.id);
        }

        if (clickedData.hasVideo) {
          this.queryVideo(this.pipeId, clickedData.id);
        }
      }
    } else if (table.isRehabilitation) {
      this.currentRehabilitationMeasure = clickedData;
    }
  }

  handleTableFocusedRowChanged(e: DxDataGridTypes.FocusedRowChangedEvent<any, any>): void {
    if (this.suspendFocusedRowChanged || !e.row?.data?.Id) {
      return;
    }

    const id = e.row.data.Id;
    const clickableFeature = this.pipeGraphicsState.clickableData.find((feature) => feature.id === id);
    const clickableImage = this.getClickableImage(this.getView());
    clickableImage.setMarking(clickableFeature);
    this.handleFeatureClicked(clickableFeature, true);
  }
  private suspendFocusedRowChanged = false;

  handleTableDataSourceChanged(e: OptionChangedEvent): void {
    if (e.name !== 'dataSource' || e.component.columnCount() > 0) {
      return;
    }

    e.component.option('columns', []);
    let isVisible = true;
    let pendingColumn: any = null;

    for (const column of this.pipeGraphicsState.table.columns) {
      if (isVisible) {
        isVisible = !!column.caption;
      }

      const gridColumn = {
        dataField: column.name,
        format: column.format,
        caption: column.caption ? column.caption : column.name,
        showInColumnChooser: !!column.caption,
        visible: isVisible,
        width: column.width,
        minWidth: column.minWidth,
      };

      if (column.isBanded) {
        if (!pendingColumn.columns) {
          pendingColumn.columns = [];
        }

        pendingColumn.columns.push(gridColumn);
        pendingColumn.alignment = 'center';
      } else {
        if (pendingColumn) {
          e.component.addColumn(pendingColumn);
        }

        pendingColumn = gridColumn;
      }
    }

    if (pendingColumn) {
      e.component.addColumn(pendingColumn);
    }
  }

  handleOuterSplitterResize(e: ResizeEvent): void {
    this.resizePipeImage();
  }

  private resizePipeImage(): void {
    const imageTabsContainer = this.getImageTabsContainer();

    if (imageTabsContainer) {
      this.getAllClickableImages().forEach((clickableImage) =>
        clickableImage.size.set({ width: imageTabsContainer.clientWidth, height: imageTabsContainer.clientHeight }),
      );
    }
  }

  private setMediaButtonEnabled(): void {
    const mediaButtonDisabled = !this.pipeGraphicsState.inspectionId && !this.showMedia;

    if (this.toolbarMediaButtonOptions.disabled !== mediaButtonDisabled) {
      this.toolbarMediaButtonOptions.disabled = mediaButtonDisabled;
      this.toolbarMediaButtonOptions.type = 'normal';
      this.resetToolbarMediaButtonOptions();
    }
  }

  private setPrintButtonEnabled(view: string): void {
    let printButtonDisabled = !this.pipeGraphicsState.reportFilename;

    if (!printButtonDisabled) {
      if (view === StringLiteral.ViewSocketBranch) {
        printButtonDisabled = !this.pipeGraphicsSettings.reportDocumentIdSocketBranch;
      } else if (this.pipeGraphicsState.additionalId) {
        printButtonDisabled = this.pipeGraphicsState.rehabilitationId
          ? !this.pipeGraphicsSettings.reportDocumentIdInspectionRehabilitation
          : !this.pipeGraphicsSettings.reportDocumentIdDoubleInspection;
      } else {
        printButtonDisabled = this.pipeGraphicsState.rehabilitationId
          ? !this.pipeGraphicsSettings.reportDocumentIdSingleRehabilitation
          : !this.pipeGraphicsSettings.reportDocumentIdSingleInspection;
      }
    }

    if (this.toolbarPrintButtonOptions.disabled !== printButtonDisabled) {
      this.toolbarPrintButtonOptions.disabled = printButtonDisabled;
      this.resetToolbarPrintButtonOptions();
    }
  }

  private changeView(view: string): void {
    switch (view) {
      case StringLiteral.ViewSocketBranch:
        this.tabPanel().selectedIndex = 0;
        break;
      case StringLiteral.ViewInspection:
        this.tabPanel().selectedIndex = 1;
        break;
    }
  }

  private getView(): string {
    switch (this.tabPanel().selectedIndex) {
      case 0:
        return StringLiteral.ViewSocketBranch;
      case 1:
        return StringLiteral.ViewInspection;
      case 2:
    }

    throw new Error('invalid operation');
  }

  private getClickableImage(view: string): ScalableImageComponent<ClickableData> {
    switch (view) {
      case StringLiteral.ViewSocketBranch:
        return this.scalableImageSocketBranch();
      case StringLiteral.ViewInspection:
        return this.scalableImageInspection();
    }

    throw new Error('invalid operation');
  }

  private getAllClickableImages(): ScalableImageComponent<ClickableData>[] {
    return [this.scalableImageSocketBranch(), this.scalableImageInspection()];
  }

  private getDataGrid(table: PipeGraphicsTable): _AbcGridComponent {
    if (table.isPipe) {
      return this.gridConnectedPipes();
    } else if (table.isInspection) {
      return this.gridInspectionTexts();
    } else if (table.isRehabilitation) {
      return this.gridRehabilitationMeasures();
    }

    throw new Error('invalid operation');
  }

  private resetAllFocusedRows(): void {
    this.resetFocusedRow(this.gridConnectedPipes(), this.gridInspectionTexts(), this.gridRehabilitationMeasures());
  }

  private resetFocusedRow(...dataGrids: _AbcGridComponent[]): void {
    this.suspendFocusedRowChanged = true;

    try {
      for (const dataGrid of dataGrids) {
        dataGrid.focusedRowKey = undefined;
      }
    } finally {
      this.suspendFocusedRowChanged = false;
    }
  }

  private resetToolbarPrintButtonOptions(): void {
    const toolbarPrintButtonOptions = this.toolbarPrintButtonOptions;
    this.toolbarPrintButtonOptions = {};
    setTimeout(() => (this.toolbarPrintButtonOptions = toolbarPrintButtonOptions));
  }

  private resetToolbarTableButtonOptions(): void {
    const toolbarTableButtonOptions = this.toolbarTableButtonOptions;
    this.toolbarTableButtonOptions = {};
    setTimeout(() => (this.toolbarTableButtonOptions = toolbarTableButtonOptions));
  }

  private resetToolbarMediaButtonOptions(): void {
    const toolbarMediaButtonOptions = this.toolbarMediaButtonOptions;
    this.toolbarMediaButtonOptions = {};
    setTimeout(() => (this.toolbarMediaButtonOptions = toolbarMediaButtonOptions));
  }

  private wasMaximizedOrUnmaximized(element: Element): boolean | null {
    const tolerance = 10;
    const domRect = element.getBoundingClientRect();

    try {
      if (this.lastDOMRect) {
        if (
          domRect.width <= window.innerWidth &&
          window.innerWidth - domRect.width < tolerance &&
          domRect.height <= window.innerHeight &&
          window.innerHeight - domRect.height < tolerance &&
          domRect.width > this.lastDOMRect.width + tolerance &&
          domRect.height > this.lastDOMRect.height + tolerance
        ) {
          return true; // was maximized
        } else if (
          this.lastDOMRect.width <= window.innerWidth &&
          window.innerWidth - this.lastDOMRect.width < tolerance &&
          this.lastDOMRect.height <= window.innerHeight &&
          window.innerHeight - this.lastDOMRect.height < tolerance &&
          domRect.width < this.lastDOMRect.width - tolerance &&
          domRect.height < this.lastDOMRect.height - tolerance
        ) {
          return false; // was unmaximized
        }
      }

      return null;
    } finally {
      this.lastDOMRect = domRect;
    }
  }

  private getImageTabsContainer(): any {
    let imageTabsContainer: any = undefined;
    const tabPanel = this.tabPanel().instance.element();

    if (tabPanel.children.length > 1) {
      imageTabsContainer = tabPanel.children[1];

      for (let i = 0; i < 2; i++) {
        if (imageTabsContainer.children.length) {
          imageTabsContainer = imageTabsContainer.children[0];
        } else {
          imageTabsContainer = undefined;
          break;
        }
      }
    }

    return imageTabsContainer;
  }

  getPopupWidth(): string {
    if (window.innerWidth <= 768) {
      return '50%';
    } else if (window.innerWidth <= 1024) {
      return '80%';
    }
    return '500px';
  }

  getPopupHeight(): string {
    if (window.innerWidth <= 768) {
      return '49%';
    } else if (window.innerWidth <= 1024) {
      return '330px';
    }
    return '380px';
  }
}
